import { useTranslation } from 'react-i18next';
import { SEO } from '../components/SEO';
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Users, Crown, UserCheck, User, FileText, Target, ChevronRight, Shield } from 'lucide-react';
import { GrasrotandelenBanner } from '../components/GrasrotandelenBanner';

const boardMembers = [
  { title: 'board.roles.chair', name: 'Sven Anders Sjøblom Stensø', icon: Crown },
  { title: 'board.roles.viceChair', name: 'Haris Nuhanovic', icon: UserCheck },
  { title: 'board.roles.member', name: 'Lisa Marie Rognlien', icon: User },
  { title: 'board.roles.member', name: 'Maj-Lis Kesti', icon: User },
  { title: 'board.roles.member', name: 'Kristine Eikenes', icon: User },
  { title: 'board.roles.member', name: 'Per Atle Aaslid', icon: User },
  { title: 'board.roles.member', name: 'Robin Granum Nguyen', icon: User },
  { title: 'board.roles.deputyRentals', name: 'Vera Kristiansen', icon: User },
  { title: 'board.roles.deputy', name: 'Michal Stefanczak', icon: User },
];

export const Styret = () => {
  const { t } = useTranslation();
  return (
    <div data-testid="styret-page" className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <SEO title={t('board.title')} />
      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-800">
        <div className="max-w-4xl mx-auto">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-slate-500 hover:text-green-600 transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('common.backToHome')}
          </Link>
          
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center">
              <Users className="w-7 h-7 text-green-600" />
            </div>
            <div>
              <p className="text-green-600 font-medium uppercase tracking-widest mb-1">{t('board.label')}</p>
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tighter text-slate-900 dark:text-white">
                {t('board.title')}
              </h1>
            </div>
          </div>
          
          <p className="text-slate-600 text-lg">
            {t('board.intro')}
          </p>
        </div>
      </section>

      {/* Board Members Grid */}
      <section className="px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-4xl mx-auto">
          <div className="grid sm:grid-cols-2 gap-4">
            {boardMembers.map((member, index) => {
              const IconComponent = member.icon;
              const isLeader = member.title === 'Styrets leder';
              const isViceLeader = member.title === 'Nestleder';
              
              return (
                <div
                  key={index}
                  data-testid={`board-member-${index}`}
                  className={`
                    bg-white rounded-xl border transition-all duration-300 shadow-sm
                    ${isLeader 
                      ? 'border-green-500 sm:col-span-2' 
                      : isViceLeader 
                        ? 'border-green-300' 
                        : 'border-slate-200 hover:border-slate-300'
                    }
                  `}
                >
                  <div className={`p-6 ${isLeader ? 'sm:flex sm:items-center sm:gap-6' : ''}`}>
                    <div className={`
                      w-12 h-12 rounded-xl flex items-center justify-center mb-4
                      ${isLeader 
                        ? 'bg-green-500 sm:mb-0' 
                        : isViceLeader 
                          ? 'bg-green-100' 
                          : 'bg-slate-100'
                      }
                    `}>
                      <IconComponent className={`w-6 h-6 ${isLeader ? 'text-white' : 'text-green-600'}`} />
                    </div>
                    <div>
                      <p className={`
                        text-xs font-medium uppercase tracking-wider mb-1
                        ${isLeader ? 'text-green-600' : 'text-slate-500'}
                      `}>
                        {t(member.title)}
                      </p>
                      <p className={`
                        font-bold
                        ${isLeader ? 'text-2xl text-slate-900' : 'text-lg text-slate-900'}
                      `}>
                        {member.name}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Related Documents Section */}
      <section className="px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
            <FileText className="w-5 h-5 text-green-600" />
            {t('board.documents')}
          </h2>
          <div className="grid sm:grid-cols-3 gap-4">
            <Link
              to="/aarsmoeteprotokoll"
              data-testid="link-aarsmoeteprotokoll"
              className="bg-white rounded-xl border border-slate-200 hover:border-green-500 p-6 transition-all duration-300 group flex items-center justify-between shadow-sm hover:shadow-md"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-slate-100 group-hover:bg-green-100 flex items-center justify-center transition-colors">
                  <FileText className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-slate-900 font-bold">{t('board.agmMinutes')}</p>
                  <p className="text-slate-500 text-sm">{t('board.agmMinutesDesc')}</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-green-600 transition-colors" />
            </Link>
            
            <Link
              to="/strategi-sportsplan"
              data-testid="link-strategi-sportsplan"
              className="bg-white rounded-xl border border-slate-200 hover:border-green-500 p-6 transition-all duration-300 group flex items-center justify-between shadow-sm hover:shadow-md"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-slate-100 group-hover:bg-green-100 flex items-center justify-center transition-colors">
                  <Target className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-slate-900 font-bold">{t('board.strategy')}</p>
                  <p className="text-slate-500 text-sm">{t('board.strategyDesc')}</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-green-600 transition-colors" />
            </Link>
            
            <Link
              to="/politiattest"
              data-testid="link-politiattest"
              className="bg-white rounded-xl border border-slate-200 hover:border-green-500 p-6 transition-all duration-300 group flex items-center justify-between shadow-sm hover:shadow-md"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-slate-100 group-hover:bg-green-100 flex items-center justify-center transition-colors">
                  <Shield className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-slate-900 font-bold">{t('board.policeCert')}</p>
                  <p className="text-slate-500 text-sm">{t('board.policeCertDesc')}</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-green-600 transition-colors" />
            </Link>
          </div>
        </div>
      </section>

      {/* Grasrotandelen Banner */}
      <GrasrotandelenBanner />
    </div>
  );
};

export default Styret;
